/**
 * @fileoverview In-memory audit events for the audit mock.
 * @description Generates a deterministic set of audit events, shaped like the real API's: the
 * backend's own event types, categories and source modules, display names and emails masked the way
 * the API masks them without AUDIT.SENSITIVE_DATA.READ, masked IPs, and no tenant on platform-level
 * events. Times are spread over the last three weeks, counted back from when the module loads, so
 * the page's default last-7-days range always has data. A seeded generator (not Math.random) keeps
 * the same event in the same place every load.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type {
  AuditEventChange,
  AuditEventDetail,
  AuditEventSummary,
  AuditResult,
  AuditTenantRef,
} from '@/features/audit/types/audit.types'

type ActorKind = 'user' | 'system' | 'service'

interface EventTemplate {
  eventType: string
  eventCategory: string
  sourceModule: string
  result: AuditResult
  actor: ActorKind
  resource?: { resourceType: string; idPrefix: string }
  tenantScoped?: boolean
  changes?: AuditEventChange[]
  failureReason?: string
  metadata?: Record<string, unknown>
}

interface MockPerson {
  userId: string
  name: string
  email: string
}

const EVENT_COUNT = 160
const SPAN_MS = 21 * 24 * 60 * 60 * 1000

const PEOPLE: MockPerson[] = [
  { userId: 'u-1', name: 'Hrishikesh Patwardhan', email: 'hrishikesh@insightbox.tech' },
  { userId: 'u-2', name: 'Hardik Mehta', email: 'hardik@insightbox.tech' },
  { userId: 'u-3', name: 'Saurabh Rane', email: 'saurabh@insightbox.tech' },
  { userId: 'u-4', name: 'Rahul Kulkarni', email: 'rahul@insightbox.tech' },
  { userId: 'u-10', name: 'Vikram Desai', email: 'vikram@insightbox.in' },
]

const TENANTS: AuditTenantRef[] = [
  { tenantId: 'tenant-hsh', tenantName: 'HSH Co-operative Bank Ltd.' },
  { tenantId: 'tenant-sahyadri', tenantName: 'Sahyadri Urban Co-operative Bank' },
]

const DEVICES = [
  {
    deviceType: 'DESKTOP',
    browserName: 'Chrome',
    browserVersion: '141',
    osName: 'Windows',
    osVersion: '11',
  },
  {
    deviceType: 'DESKTOP',
    browserName: 'Edge',
    browserVersion: '154',
    osName: 'Windows',
    osVersion: '11',
  },
  {
    deviceType: 'DESKTOP',
    browserName: 'Safari',
    browserVersion: '19',
    osName: 'macOS',
    osVersion: '15',
  },
  {
    deviceType: 'MOBILE',
    browserName: 'Chrome',
    browserVersion: '141',
    osName: 'Android',
    osVersion: '16',
  },
]

const CITIES = [
  { country: 'IN', region: 'Maharashtra', city: 'Pune' },
  { country: 'IN', region: 'Maharashtra', city: 'Mumbai' },
  { country: 'IN', region: 'Maharashtra', city: 'Nagpur' },
  { country: 'IN', region: 'Karnataka', city: 'Bengaluru' },
]

const TEMPLATES: EventTemplate[] = [
  {
    eventType: 'USER_LOGIN_SUCCEEDED',
    eventCategory: 'AUTHENTICATION',
    sourceModule: 'identity',
    result: 'SUCCESS',
    actor: 'user',
  },
  {
    eventType: 'USER_LOGIN_SUCCEEDED',
    eventCategory: 'AUTHENTICATION',
    sourceModule: 'identity',
    result: 'SUCCESS',
    actor: 'user',
  },
  {
    eventType: 'USER_LOGIN_FAILED',
    eventCategory: 'AUTHENTICATION',
    sourceModule: 'identity',
    result: 'FAILED',
    actor: 'user',
    failureReason: 'INVALID_CREDENTIALS',
  },
  {
    eventType: 'PASSWORD_CHANGED',
    eventCategory: 'IDENTITY',
    sourceModule: 'identity',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'USER', idPrefix: 'u-' },
  },
  {
    eventType: 'SESSION_REVOKED',
    eventCategory: 'IDENTITY',
    sourceModule: 'identity',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'SESSION', idPrefix: 'session-' },
  },
  {
    eventType: 'ACCOUNT_LOCKED',
    eventCategory: 'IDENTITY',
    sourceModule: 'identity',
    result: 'SUCCESS',
    actor: 'system',
    resource: { resourceType: 'USER', idPrefix: 'u-' },
  },
  {
    eventType: 'SESSION_IP_CHANGED',
    eventCategory: 'IDENTITY',
    sourceModule: 'identity',
    result: 'SUCCESS',
    actor: 'system',
    metadata: { previousIpMasked: '203.0.113.xxx', firstSeenAt: '2026-10-21T12:00:00.000Z' },
  },
  {
    eventType: 'USER_INVITED',
    eventCategory: 'USER_MANAGEMENT',
    sourceModule: 'users',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'USER_INVITATION', idPrefix: 'invitation-' },
  },
  {
    eventType: 'USER_STATUS_CHANGED',
    eventCategory: 'USER_MANAGEMENT',
    sourceModule: 'users',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'USER', idPrefix: 'u-' },
    changes: [{ field: 'status', before: 'ACTIVE', after: 'INACTIVE' }],
  },
  {
    eventType: 'USER_REPORTING_LINE_CREATED',
    eventCategory: 'USER_MANAGEMENT',
    sourceModule: 'users',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'USER', idPrefix: 'u-' },
    changes: [{ field: 'reportingManagerUserId', before: 'u-2', after: 'u-3' }],
  },
  {
    eventType: 'PERMISSION_GRANTED',
    eventCategory: 'AUTHORIZATION',
    sourceModule: 'authorization',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'ROLE_PERMISSION', idPrefix: 'role-permission-' },
  },
  {
    eventType: 'ASSIGNMENT_REVOKED',
    eventCategory: 'AUTHORIZATION',
    sourceModule: 'authorization',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'USER_ROLE_ASSIGNMENT', idPrefix: 'assignment-' },
    changes: [{ field: 'status', before: 'ACTIVE', after: 'REVOKED' }],
  },
  {
    eventType: 'ACCESS_DENIED',
    eventCategory: 'AUTHORIZATION',
    sourceModule: 'authorization',
    result: 'DENIED',
    actor: 'user',
    failureReason: 'SCOPE_NOT_AUTHORIZED',
  },
  {
    eventType: 'TENANT_CREATED',
    eventCategory: 'TENANT_MANAGEMENT',
    sourceModule: 'tenants',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'TENANT', idPrefix: 'tenant-' },
    tenantScoped: true,
  },
  {
    eventType: 'PROSPECT_OWNER_ASSIGNED',
    eventCategory: 'SALES_CRM',
    sourceModule: 'sales-crm',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'PROSPECT', idPrefix: 'prospect-' },
    tenantScoped: true,
    changes: [{ field: 'ownerUserId', before: 'u-3', after: 'u-4' }],
  },
  {
    eventType: 'APPROVAL_REJECTED',
    eventCategory: 'APPROVAL',
    sourceModule: 'approvals',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'APPROVAL_REQUEST', idPrefix: 'approval-' },
  },
  {
    eventType: 'NOTIFICATION_FAILED',
    eventCategory: 'NOTIFICATION',
    sourceModule: 'notifications',
    result: 'FAILED',
    actor: 'service',
    failureReason: 'PROVIDER_REJECTED',
  },
  {
    eventType: 'AUDIT_SEARCH_PERFORMED',
    eventCategory: 'AUDIT',
    sourceModule: 'audit',
    result: 'SUCCESS',
    actor: 'user',
  },
  {
    eventType: 'AUDIT_EVENT_VIEWED',
    eventCategory: 'AUDIT',
    sourceModule: 'audit',
    result: 'SUCCESS',
    actor: 'user',
    resource: { resourceType: 'AUDIT_EVENT', idPrefix: 'audit-' },
  },
]

/**
 * @description Builds a seeded pseudo-random generator (mulberry32), so every load produces the
 * same events.
 * @param {number} seed - The starting seed.
 * @returns {() => number} A function returning the next number in [0, 1).
 */
function seeded(seed: number): () => number {
  let state = seed
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * @description Masks a display name the way the API does: each word keeps its first letter.
 * @param {string} name - The full name.
 * @returns {string} E.g. "Hardik Mehta" becomes "H***** M****".
 */
function maskName(name: string): string {
  return name
    .split(' ')
    .map((word) => word.charAt(0) + '*'.repeat(Math.max(0, word.length - 1)))
    .join(' ')
}

/**
 * @description Masks an email the way the API does without AUDIT.SENSITIVE_DATA.READ.
 * @param {string} email - The email address.
 * @returns {string} E.g. "h*****@insightbox.tech".
 */
function maskEmail(email: string): string {
  const [local = '', domain = ''] = email.split('@')
  return `${local.charAt(0)}*****@${domain}`
}

/**
 * @description Builds every mock audit event, newest first.
 * @returns {AuditEventDetail[]} The events.
 */
function buildEvents(): AuditEventDetail[] {
  const random = seeded(20261028)
  const now = Date.now()
  const pick = <T>(items: readonly T[]): T => items[Math.floor(random() * items.length)] as T

  const events = Array.from({ length: EVENT_COUNT }, (_, index): AuditEventDetail => {
    const template = pick(TEMPLATES)
    const person = pick(PEOPLE)
    const occurredAt = new Date(now - (index / EVENT_COUNT) * SPAN_MS - random() * 3_600_000)
    const tenant = template.tenantScoped ? pick(TENANTS) : undefined
    const device = pick(DEVICES)
    const geo = pick(CITIES)
    const suffix = String(index + 1).padStart(12, '0')
    const resourceId = template.resource
      ? template.resource.idPrefix +
        (template.resource.idPrefix === 'u-' ? pick(PEOPLE).userId.slice(2) : 500 + index)
      : undefined

    const actor =
      template.actor === 'user'
        ? {
            actorType: 'USER' as const,
            actorId: person.userId,
            displayName: maskName(person.name),
            email: maskEmail(person.email),
          }
        : template.actor === 'service'
          ? { actorType: 'SERVICE' as const, actorId: `svc-${template.sourceModule}` }
          : { actorType: 'SYSTEM' as const }

    return {
      auditEventId: `3f2b6c1e-8d4a-4f0b-9a57-${suffix}`,
      schemaVersion: 'v1',
      occurredAt: occurredAt.toISOString(),
      eventCategory: template.eventCategory,
      eventType: template.eventType,
      sourceModule: template.sourceModule,
      result: template.result,
      actor,
      ...(tenant && { tenant }),
      ...(template.resource &&
        resourceId && { resource: { resourceType: template.resource.resourceType, resourceId } }),
      ...(template.changes && { changes: template.changes }),
      correlation: {
        correlationId: `corr-${index + 1}`,
        requestId: `req-${index + 1}`,
        traceId: random().toString(16).slice(2).padEnd(32, '0').slice(0, 32),
      },
      ...(template.actor === 'user' && { session: { sessionId: `session-${900 + index}` } }),
      ...(template.actor === 'user' && {
        device: { deviceId: `7ed1b478-f3d8-466f-9216-${suffix}`, ...device },
      }),
      ...(template.actor === 'user' && {
        network: {
          ipAddress: `10.25.${Math.floor(random() * 200)}.xxx`,
          userAgent: `Mozilla/5.0 (${device.osName}) ${device.browserName}/${device.browserVersion}`,
        },
        geo: { ...geo, source: 'dbip-city-lite-2026-10' },
      }),
      ...(template.failureReason && { failure: { reasonCode: template.failureReason } }),
      ...(template.metadata && { metadata: template.metadata }),
      sensitiveDataRevealed: false,
    }
  })

  return events.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
}

const EVENTS: AuditEventDetail[] = buildEvents()

/**
 * @description Every mock event, newest first.
 * @returns {AuditEventDetail[]} The events.
 */
export function listAuditEvents(): AuditEventDetail[] {
  return EVENTS
}

/**
 * @description Finds one mock event by id.
 * @param {string} auditEventId - The event id.
 * @returns {AuditEventDetail | undefined} The event, if it exists.
 */
export function findAuditEvent(auditEventId: string): AuditEventDetail | undefined {
  return EVENTS.find((event) => event.auditEventId === auditEventId)
}

/**
 * @description Reduces a detail to the summary row the search returns: no email, IP, device,
 * changes, metadata or failure.
 * @param {AuditEventDetail} event - The full event.
 * @returns {AuditEventSummary} The summary.
 */
export function toSummary(event: AuditEventDetail): AuditEventSummary {
  const { actorType, actorId, displayName } = event.actor
  return {
    auditEventId: event.auditEventId,
    occurredAt: event.occurredAt,
    eventCategory: event.eventCategory,
    eventType: event.eventType,
    sourceModule: event.sourceModule,
    result: event.result,
    actor: { actorType, ...(actorId && { actorId }), ...(displayName && { displayName }) },
    ...(event.tenant && { tenant: event.tenant }),
    ...(event.resource && { resource: event.resource }),
    ...(event.correlation?.correlationId && { correlationId: event.correlation.correlationId }),
    ...(event.session && { sessionId: event.session.sessionId }),
  }
}
